import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, forwardRef, inject } from '@angular/core';

import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ReadService } from '@common/service/read.service';
import TmwTranslatePipe from '@shared/pipes/translate.pipe';
import { TmwFilterPipe } from '../../pipes/filter.pipe';
import { TmwTranslateDirective } from '@shared/directives/translate.directive';

const noop = () => {
  return;
};

const MULTISELECT_CONTROL_VALUE_ACCESSOR = {
  multi: true,
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MultiSelectEditComponent),
};

@Component({
  standalone: true,
  providers: [MULTISELECT_CONTROL_VALUE_ACCESSOR],
  selector: 'tmw-multi-select-edit',
  templateUrl: './multi-select-edit.component.html',
  imports: [CommonModule, FormsModule, TmwFilterPipe, forwardRef(() => TmwTranslateDirective)]
})
export class MultiSelectEditComponent implements ControlValueAccessor, OnInit {
  filter = false;
  term = '';
  filter2 = false;
  term2 = '';

  readService = inject(ReadService);
  private readonly destroy: DestroyRef = inject(DestroyRef);

  @Input() runIf = true;

  @Input() id: string = '';
  @Input() name = '';
  @Input() enableFilter = true;
  disableInput = false;

  @Input() fromTitleKey: string = 'anc.multiSelect.fromList';
  @Input() toTitleKey: string = 'anc.multiSelect.toList';
  _list: any[] = [];

  @Input() set list(lst: any[]) {
    this._list = lst;
    if (this.init) {
      this.loadAll();
    }
  }

  get list() {
    return this._list;
  }

  filtrer = false;
  filtrer2 = false;

  @Input() showFilter = false;

  @Input() changeAssignment = true;

  @Input() keyField: string = 'id';
  @Input() labelFiled: string = 'libelle';

  @Input() isSaving: boolean = false;

  @Input() refType: string | undefined;

  @Output() assigned = new EventEmitter<any[]>();
  @Output() unassigned = new EventEmitter<any[]>();

  vals: any[] = [];

  init = false;

  private onTouch: () => void = noop;
  private onChange: (_: any) => void = noop;

  habilitationParams: any = {};
  assignedElements: any[] = [];
  unassignedElements: any[] = [];

  toAssignList: any[] = [];
  toUnassignList: any[] = [];

  isSelectedR: boolean = false;
  isSelectedRA: boolean = false;

  ngOnInit() {
    if (this.refType && this.runIf) {
      this.readService.queryRef(this.refType)
        .pipe(takeUntilDestroyed(this.destroy))
        .subscribe((result) => {
          if (result) {
            this.list = result;
            this.loadAll();
          }
        });
    } else {
      this.loadAll();
    }
    this.init = true;
  }

  loadAll() {
    this.clearSelection();
    this.loadAssignments();
  }

  /** Décoche tous les éléments des deux listes et vide les sélections en cours. */
  clearSelection() {
    (this.list ?? []).forEach((e) => (e.selected = false));
    this.toAssignList = [];
    this.toUnassignList = [];
  }

  loadAssignments() {
    this.assignedElements = this.list.filter((e) => this.vals.some((se) => se == e[this.keyField]));
    this.unassignedElements = this.list.filter((e) => !this.vals.some((se) => se == e[this.keyField]));
  }

  assignSelected() {
    if (this.toAssignList?.length > 0) {
      this.assigned.emit(this.toAssignList.map((e) => e[this.keyField]));
      if (this.changeAssignment) {
        this.assignedElements = [...this.assignedElements, ...this.toAssignList];
        this.unassignedElements = this.unassignedElements.filter(
          (e) => !this.toAssignList.some((se) => se[this.keyField] == e[this.keyField]),
        );
        this.updateVals();
      }
      // Toujours décocher, y compris quand le parent gère l'affectation (changeAssignment=false)
      this.toAssignList.forEach((e) => (e.selected = false));
      this.toAssignList = [];
    }
  }

  unassignSelected() {
    if (this.toUnassignList?.length > 0) {
      this.unassigned.emit(this.toUnassignList.map((e) => e[this.keyField]));
      if (this.changeAssignment) {
        this.unassignedElements = [...this.unassignedElements, ...this.toUnassignList];
        this.assignedElements = this.assignedElements.filter(
          (e) => !this.toUnassignList.some((se) => se[this.keyField] == e[this.keyField]),
        );
        this.updateVals();
      }
      // Toujours décocher, y compris quand le parent gère l'affectation (changeAssignment=false)
      this.toUnassignList.forEach((e) => (e.selected = false));
      this.toUnassignList = [];
    }
  }

  updateVals() {
    this.value = this.assignedElements.map((e) => e[this.keyField]);
  }

  addToList(element: any) {
    element.selected = !element.selected;
    this.toAssignList = this.unassignedElements.filter((e) => e.selected);
  }

  delFromList(element: any) {
    element.selected = !element.selected;
    this.toUnassignList = this.assignedElements.filter((e) => e.selected);
  }

  get value() {
    return this.vals;
  }
  set value(val) {
    if (this.vals !== val) {
      this.vals = val;
      this.onChange(this.vals);
      this.onTouch();
    }
  }

  public writeValue(value: any) {
    value = value || [];
    if (this.vals !== value) {
      this.vals = value || [];
      if (this.runIf && this.init) {
        this.loadAll();
      }
    }
  }

  public registerOnChange(fn: any) {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any) {
    this.onTouch = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disableInput = isDisabled;
  }
}
